// types/interfaces

import { config } from "@brand";
import { SelectOption } from "../../data/selectInput.type";

export interface ImgPurchaseResult {
  primaryCertificateNumber: string | null,
  certificateNumber: string,
  effectiveDate: string,
  expirationDate: string,
  totalPremium: number,
  insureds: {
    insuredId: string,
    effectiveDate: string,
    expirationDate: string,
    amount: number,
    tax: number,
    total: number
  }[]
  brokenRules: string[],
  errors: null
}

export interface ImgPurchase {
  producerNumber: string;
  uservar: string;
  productCode: string;
  appType: string;
  signatureName: string;
  signatureRelationship?: string;
  travelInfo: {
    startDate: string;
    endDate: string;
    destinations: string[];
    initialPaymentDate: string;
  },
  policyInfo: {
    currencyCode: string;
    fulfillmentMethod: string;
  },
  families: {
    insureds: Partial<RequestFamilyMember>[]
  }[],
  contacts: {
    contactInfoType: string;
    address: string;
    city: string;
    stateProvince: string;
    postalCode: string;
    country: string;
    phone: string;
  }[],
  groupSponsorInfo: {
    organization?: string;
  }
  paymentInfo: ImgPaymentMethod
}

export interface ImgPaymentMethod {
  paymentType: string;
  echeckType?: string;
  nameOnAccount?: string;
  routingNumber?: string;
  accountNumber?: string;
  creditCardNumber?: string;
  cardExpire?: string;
  cardCvv?: string;
}
export interface ImgAddress {
  address: string;
  address2?: string;
  city: string;
  stateProvince: string;
  stateObject: SelectOption | null;
  postalCode: string;
  country: string;
  countryObject: SelectOption | null;
  phone: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface PurchaseBilling {
  billingAddress?: ImgAddress;
  sameBilling: string;
  paymentMethod: string;
  cardName?: string;
  cardNumber?: string;
  expiryDate?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cvc?: string;
  accountType?: string;
  accountName?: string;
  routingNumber?: string;
  accountNumber?: string;
  agreeCharge: boolean;
  digitalSignature: string;
  signatureRelationship?: string;
}

export interface PurchaseDetails {
  startDate: string | Date | null;
  endDate: string | Date | null;
  usaTrip: string | null;
  country?: string | null;
  countryObject?: SelectOption | null;
  tripProtection?: string | null;
  useOverallTripCost?: string | null;
  overallTripCost: string;
  initialPaymentDate?: Date | string;
  haveSponsoringOrganization?: string | null;
  sponsoringOrganization?: string | null;
  tripCost: string;
  primaryInsured: Partial<PrimaryInsured>[]
  saveQuote?: boolean;
  shippingAddress?: ImgAddress;
  appType?: string;
  productCode?: string;
  residencyState?: string;
  residencyCountry?: string;
}

export interface RequestFamilyMember {
  firstName?: string | null;
  lastName?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  age: number | string;
  dateOfBirth?: string | Date | null;
  gender?: string;
  citizenship?: string;
  residencyCountry?: string;
  residencyState?: string;
  genderObject: SelectOption | null;
  citizenshipObject: SelectOption | null;
  residencyCountryObject: SelectOption | null;
  residencyStateObject: SelectOption | null;
  tripCost: string;
  email?: string;
}

export interface PrimaryInsured extends RequestFamilyMember {
  spouse: Partial<RequestFamilyMember>;
  hasSpouse: boolean;
  hasChildren: boolean;
  children: Partial<RequestFamilyMember>[]
}

// default form values

export const familyMemberValues: RequestFamilyMember = {
  firstName: '',
  lastName: '',
  age: '',
  tripCost: '',
  genderObject: null,
  citizenshipObject: null,
  residencyCountryObject: null,
  residencyStateObject: null,
  dateOfBirth: null,
  email: ''
}

export const initialPrimaryInsuredValues: PrimaryInsured = {
  ...familyMemberValues,
  hasSpouse: false,
  hasChildren: false,
  spouse: familyMemberValues,
  children: [familyMemberValues]
}

export const initialAddressValues: ImgAddress = {
  address: '',
  address2: '',
  city: '',
  stateProvince: '',
  stateObject: null,
  postalCode: '',
  country: '',
  countryObject: null,
  phone: '',
  email: ''
}

export const initialBillingValues: PurchaseBilling = {
  sameBilling: 'sameAddress',
  paymentMethod: 'card',
  cardName: '',
  cardNumber: '',
  expiryDate: '',
  expiryMonth: '',
  expiryYear: '',
  cvc: '',
  accountType: 'commercialChecking',
  accountName: '',
  routingNumber: '',
  accountNumber: '',
  agreeCharge: false,
  digitalSignature: '',
  signatureRelationship: '',
  billingAddress: {
    address: '',
    address2: '',
    city: '',
    stateProvince: '',
    stateObject: null,
    postalCode: '',
    country: '',
    countryObject: null,
    phone: '',
    firstName: '',
    lastName: ''
  }
};

export const initialDetailsValues: PurchaseDetails = {
  startDate: null,
  endDate: null,
  usaTrip: '',
  countryObject: { label: 'Select a Country', value: 'none' },
  tripProtection: 'yes',
  useOverallTripCost: 'yes',
  overallTripCost: '',
  initialPaymentDate: '',
  haveSponsoringOrganization: config.allowUseSponsorship ? 'yes' : 'no',
  sponsoringOrganization: '',
  primaryInsured: [],
  tripCost: '',
  residencyState: '',
  residencyCountry: '',
  appType: '',
  productCode: ''
};